/**
 * Rail class representing a horizontal content rail.
 * Manages a collection of content items (events, shows, addons, corners, etc.)
 * displayed in a horizontal scrolling rail with pagination, sorting, and display mode support.
 */

import Addon from './addon';
import AppEvent from './appEvent';
import Backdoor from './backdoor';
import Category from './category';
import Content from '../types/enums/content';
import Corner from './corner';
import EpgEvent from './epgEvent';
import FastEvent from './fastEvent';
import IACorner from './iacorner';
import MediaProduction from './mediaProduction';
import RegularEvent from './regularEvent';
import TranslationManager from '@shared/assets/locale/translationsManager';
import { IRailData } from '../types/interfaces/rail';
import {
  IAddonEventRequest,
  IAppEventRequest,
  ICornerEventRequest,
  IIACornerEventRequest,
  IEPGEventRequest,
  IFastEventRequest,
  IMediaProductionEventRequest,
} from '../types/interfaces/event';
import {
  RailDisplayMode,
  RailOrderSort,
  RailOrderType,
  RailType,
} from '../types/enums/rail';

/**
 * Rail class for managing horizontal content rails.
 * Handles content items, pagination, display modes, and sorting functionality.
 */
class Rail {
  data: (
    | Addon
    | AppEvent
    | Backdoor
    | Category
    | Corner
    | EpgEvent
    | FastEvent
    | IACorner
    | MediaProduction
    | RegularEvent
    | undefined
  )[]; // Elements of the rail
  anchor?: EpgEvent | FastEvent | MediaProduction | RegularEvent;
  circular?: boolean; // If the rail is circular
  displayMode: RailDisplayMode; // How to paint the rail
  dummy?: boolean; // If is a dummy rail to show while loading the real rail
  fixed?: boolean; // Whether the rail should displace on focus change or not
  id: string; // Id of the Rail
  pagination?: {
    currentPage: number;
    totalPages: number;
    verticalIndex: number; // Vertical page of the rail current rail if chunks had a unitary size
    verticalPosition: number; // Vertical position after sorting all rails
  }; // Rail pagination object
  ranking: boolean; // If the rail has been generated using AI
  reelIA: boolean; // If the rail has been generated using AI
  reloadable: boolean; // If the content of the rail can be changed while on the page
  staticRail: boolean; // If events of this rail should be updated
  title: string; // Title of the rail
  type: RailType;

  constructor(
    request: IRailData,
    data?:
      | (
          | Addon
          | Category
          | Corner
          | EpgEvent
          | FastEvent
          | MediaProduction
          | RegularEvent
          | undefined
        )[]
      | null,
    type: RailType = RailType.REGULAR,
    fixed = false,
    anchor = false
  ) {
    this.anchor =
      anchor && data
        ? (data[0] as EpgEvent | FastEvent | MediaProduction | RegularEvent)
        : undefined;
    this.circular = false; // Check rail type when necessary
    this.displayMode =
      (request.section.presentationType as RailDisplayMode) ||
      RailDisplayMode.POSTER;
    this.dummy = request.dummy || false;
    this.fixed = fixed;
    this.id = request.section.name;
    this.pagination = request.section.pagination;
    this.ranking = [
      RailDisplayMode.RANKING_PHOTO,
      RailDisplayMode.RANKING_POSTER,
    ].includes(request.section.presentationType as RailDisplayMode);
    this.reloadable = request.reloadable || false;
    this.reelIA = request.section.reelIA || false;
    this.staticRail = request.staticRail || false;
    this.title = request.section.title || '';
    if (
      request.section.titles &&
      request.section.titles[TranslationManager.currentLanguage as 'es']
    ) {
      this.title =
        request.section.titles[TranslationManager.currentLanguage as 'es'];
    }
    this.type = type;
    this.data =
      data ||
      request?.shows?.data?.map((item, index) => {
        if (this.ranking) item.position = index + 1;
        item.rail = type;
        item.seasonEpisodes = request?.shows?.totalEpisodes;
        switch (item.kind) {
          case Content.ADDON:
            this.displayMode = RailDisplayMode.ADDON_PHOTO;
            // Hide adult addons on Smart TV
            if ((item as unknown as IAddonEventRequest).controlParental) {
              return undefined;
            }
            return new Addon(item as unknown as IAddonEventRequest);

          case Content.APP:
            return new AppEvent(item as unknown as IAppEventRequest);

          case Content.BACK_DOOR:
            return new Backdoor(item as unknown as IIACornerEventRequest);

          case Content.CORNER:
            this.displayMode = RailDisplayMode.CLEAN_PHOTO;
            return new Corner(item as unknown as ICornerEventRequest);

          case Content.IA_CORNER:
            this.displayMode =
              request.section.presentationType === RailDisplayMode.CHARACTER
                ? RailDisplayMode.CHARACTER
                : RailDisplayMode.CLEAN_PHOTO;
            return new IACorner(item as unknown as IIACornerEventRequest);

          case Content.EPG_EVENT:
            return new EpgEvent(item as unknown as IEPGEventRequest);

          case Content.MEDIA_PRODUCTION:
            return new MediaProduction(
              item as IMediaProductionEventRequest,
              type === RailType.RECORDING
            );

          case Content.FAST_EVENT:
          default:
            return new FastEvent(item as IFastEventRequest);
        }
      }) ||
      [];
  }

  static constructorWithData(
    base: IRailData,
    data: (
      | Addon
      | Category
      | EpgEvent
      | FastEvent
      | MediaProduction
      | undefined
    )[],
    type: RailType = RailType.REGULAR,
    fixed = false,
    anchor = false
  ) {
    return new Rail(base, data, type, fixed, anchor);
  }

  sortRail(orderField: string, orderSort: string) {
    this.data = this.data.filter((item) => {
      if (item instanceof Category) {
        return true;
      } else {
        return item?.channel;
      }
    });
    if (orderField === RailOrderType.RATING) {
      this.data = this.data.sort((item1, item2) => {
        if (orderSort === RailOrderSort.ASCENDING) {
          return (item1?.score || 0) - (item2?.score || 0);
        } else if (orderSort === RailOrderSort.DESCENDING) {
          return (item2?.score || 0) - (item1?.score || 0);
        }
        return 0;
      });
    }
    if (orderField === RailOrderType.DATE) {
      this.data = this.data.sort((item1, item2) => {
        if (orderSort === RailOrderSort.ASCENDING) {
          return (item1?.date?.start || 0) > (item2?.date?.start || 0) ? 1 : -1;
        } else if (orderSort === RailOrderSort.DESCENDING) {
          return (item2?.date?.start || 0) > (item1?.date?.start || 0) ? 1 : -1;
        }
        return 0;
      });
    }
  }
}

export default Rail;
